import React, { useState } from "react";
import { updatePost, IPost, uploadImageToStorage } from "@/lib/community";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortableMediaItem from "@/components/community/SortableMediaItem";
import { cn } from "@/lib/utils";
import { Video, ImagePlus, X } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { getEmbeddedVideoUrl } from "@/utils/getEmbed";
import { useTranslation } from "react-i18next";

interface PostEditFormProps {
  post: IPost;
  onSave: (post: IPost) => void;
  onCancel: () => void;
}

type MediaItem = { id: string; type: "image" | "video"; url: string };

export const PostEditForm: React.FC<PostEditFormProps> = ({
  post,
  onSave,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [title, setTitle] = useState(post.title);
  const [content, setContent] = useState(post.content);
  const [media, setMedia] = useState<MediaItem[]>(
    Array.isArray(post.media)
      ? post.media.map((m: any) => (typeof m === "string" ? JSON.parse(m) : m))
      : []
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoUrlInput, setVideoUrlInput] = useState("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    const updatedPost = {
      id: post.id,
      title,
      content,
      media: media.map((m) => JSON.stringify(m)),
    };
    const result = await updatePost(updatedPost);
    setLoading(false);
    if (result) {
      onSave(result);
    } else {
      setError(t("form.error"));
    }
  };

  const triggerImageUpload = () => fileInputRef.current?.click();

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    const fileArray = Array.from(files);
    for (const file of fileArray) {
      const imageUrl = await uploadImageToStorage(file);
      if (imageUrl) {
        setMedia((prev) => [
          ...prev,
          { id: uuidv4(), type: "image", url: imageUrl },
        ]);
      }
    }
  };

  const handleAddVideo = () => {
    const url = videoUrlInput.trim();
    if (!url) return;
    const isDirectVideo = url.match(/\.(mp4|webm|ogg)(\?.*)?$/i);
    const embedUrl = getEmbeddedVideoUrl(url);
    if (isDirectVideo || embedUrl) {
      setMedia((prev) => [
        ...prev,
        { id: uuidv4(), type: "video", url: isDirectVideo ? url : embedUrl },
      ]);
      setVideoModalOpen(false);
      setVideoUrlInput("");
    }
  };

  const handleDragStart = () => setIsDragging(true);

  const handleDragEnd = (event: any) => {
    const { active, over } = event;
    setIsDragging(false);
    if (active.id !== over?.id) {
      setMedia((items) => {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const removeMedia = (id: string) =>
    setMedia((prev) => prev.filter((i) => i.id !== id));

  return (
    <div>
      <Input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t("community.postForm.titlePlaceholder", "Заголовок")}
        className="mb-2"
      />
      <Textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder={t("community.postForm.contentPlaceholder", "Текст поста")}
        rows={4}
        className="mb-2"
      />
      <input
        type="file"
        multiple
        accept="image/*"
        ref={fileInputRef}
        onChange={handleImageUpload}
        className="hidden"
      />
      {media.length > 0 && (
        <DndContext
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={media}
            strategy={horizontalListSortingStrategy}
          >
            <div className="flex space-x-2 overflow-x-auto pb-2 mb-2">
              {media.map((item) => (
                <div key={item.id} className="relative group w-40 h-40">
                  <SortableMediaItem id={item.id}>
                    <div className="w-40 h-40">
                      {item.type === "image" ? (
                        <img
                          src={item.url}
                          alt="Uploaded"
                          className="rounded-lg w-full h-full object-cover"
                        />
                      ) : item.url.includes("embed") ? (
                        <iframe
                          src={item.url}
                          className="rounded-lg w-full h-full object-cover"
                          frameBorder="0"
                          allow="autoplay; encrypted-media"
                          allowFullScreen
                        />
                      ) : (
                        <video
                          src={item.url}
                          controls
                          className="rounded-lg w-full h-full object-cover"
                        />
                      )}
                    </div>
                  </SortableMediaItem>
                  <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                      "absolute top-2 right-2 w-6 h-6 bg-white/80 z-10",
                      isDragging && "hidden"
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeMedia(item.id);
                    }}
                  >
                    <X className="h-4 w-4 text-gray-700" />
                  </Button>
                </div>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setVideoModalOpen(true)}
          >
            <Video />
          </Button>
          <Button variant="ghost" size="icon" onClick={triggerImageUpload}>
            <ImagePlus />
          </Button>
        </div>
        {error && <div className="text-red-500 text-sm mb-2">{error}</div>}
        <div className="flex gap-2">
          <Button onClick={onCancel} variant="outline">
            {t("community.postForm.cancel")}
          </Button>
          <Button onClick={handleSave} disabled={loading || !title || !content}>
            {loading
              ? t("community.postForm.saving")
              : t("community.postForm.save")}
          </Button>
        </div>
      </div>
      {videoModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">
              {t("community.postForm.addVideo")}
            </h2>
            <Input
              placeholder="https://example.com/video"
              value={videoUrlInput}
              onChange={(e) => setVideoUrlInput(e.target.value)}
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setVideoModalOpen(false)}>
                {t("community.postForm.cancel")}
              </Button>
              <Button onClick={handleAddVideo} disabled={!videoUrlInput.trim()}>
                {t("community.postForm.insert")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
